import { useMemo } from "react";
import { useAuth } from "../../../contexts/AuthContext";

export type UserRole = "student" | "teacher" | "principal" | "admin" | "superuser";

export interface RolePermissions {
  canFilterSchools: boolean;
  canFilterStudents: boolean;
  defaultSchool?: string;
  lockedToOwnData: boolean;
}

export function useUserRole(): { role: UserRole; permissions: RolePermissions } {
  const { user, isAdmin } = useAuth();

  // Map Better Auth roles to analytics roles
  const role = useMemo((): UserRole => {
    if (!user?.role) return "student";

    // Handle all admin roles (isAdmin now includes super, admin, editor, developer, educational_cons)
    if (isAdmin) {
      return "admin";
    }

    // Map known roles
    const roleMap: Record<string, UserRole> = {
      student: "student",
      teacher: "teacher",
      principal: "principal",
      admin: "admin",
      super: "admin",
      editor: "admin",
      developer: "admin",
      educational_cons: "admin",
      superuser: "admin",
    };

    return roleMap[user.role] || "student";
  }, [user?.role, isAdmin]);

  const permissions = useMemo((): RolePermissions => {
    switch (role) {
      case "student":
        return {
          canFilterSchools: false,
          canFilterStudents: false,
          lockedToOwnData: true,
        };
      case "teacher":
        return {
          canFilterSchools: false,
          canFilterStudents: true,
          lockedToOwnData: false,
        };
      case "principal":
        return {
          canFilterSchools: false,
          canFilterStudents: true,
          lockedToOwnData: false,
        };
      case "admin":
      case "superuser":
        return {
          canFilterSchools: true,
          canFilterStudents: true,
          lockedToOwnData: false,
        };
    }
  }, [role]);

  return { role, permissions };
}
